import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";

const faqItems = [
  {
    q: "Do integrations require engineering time?",
    a: "Typically minimal. We guide setup and validate the mapping.",
  },
  {
    q: "Can we launch with one region or subset of properties first?",
    a: "Yes. Start with a pilot group, then scale.",
  },
  {
    q: "What if we use a different PMS?",
    a: "Contact us. We prioritize integrations that unlock reservation-aware automation.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function IntegrationsPage() {
  const title = "PMS Integrations | Streamline, Guesty, Akia | Bellhopt";
  const description =
    "Bellhopt connects to Streamline, Guesty, and Akia to automate pre-arrival ordering and concierge execution. Plug in once. Deliver arrival-ready stays at scale.";
  const canonical = "https://bellhopt.com/integrations/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Plug Bellhopt Into Your Stack",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "Integrations", item: canonical },
      ],
    },
  ];

  const integrations = [
    {
      name: "Streamline",
      desc: "Use reservation timing to trigger pre-arrival flows and keep stay context synchronized.",
      link: "/integrations/streamline",
    },
    {
      name: "Guesty",
      desc: "Connect reservations and guest context to power pre-arrival ordering and consistent arrival execution.",
      link: "/integrations/guesty",
    },
    {
      name: "Akia",
      desc: "Align guest communications with the arrival flow so the experience feels native and coordinated.",
      link: null,
    },
  ];

  return (
    <div className="w-full min-h-screen bg-[#F9FAFB] pt-[72px] font-segoe">
      <SeoHead
        title={title}
        description={description}
        canonical="/integrations/"
        robots="index,follow"
        ogTitle="Plug Bellhopt Into Your Stack"
        ogDescription="Bellhopt connects to your PMS and guest comms tools to automate the arrival layer. Guests order ahead. We deliver and stock."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto py-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            Plug Bellhopt Into Your Stack
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Bellhopt connects to your PMS and guest comms tools to automate the arrival layer. Guests order ahead. We deliver and stock.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
              Book A Demo
            </a>
            <Link to="/integrations/streamline" className={SECONDARY_CTA}>
              Streamline Integration
            </Link>
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="plug-and-play" title="Plug In and Play">
            <p>Integrations should reduce work, not create it. Bellhopt uses your connected systems to trigger pre-arrival outreach, timing, and execution without manual coordination.</p>
            <ul className="list-disc pl-6 mt-4">
              <li>Reservation-aware timing</li>
              <li>Portfolio-wide consistency</li>
              <li>No new operational workflow for your team</li>
            </ul>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Supported Integrations
            </h2>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {integrations.map((integration) => (
                <div key={integration.name} className="rounded-2xl border border-[#E5E7EB] p-5 bg-white/90 flex flex-col">
                  <h3 className="font-segoe font-semibold text-[20px] text-[#111827]">{integration.name}</h3>
                  <p className="mt-2 text-[15px] text-[#6B7280] leading-relaxed flex-grow">{integration.desc}</p>
                  {integration.link && (
                    <Link to={integration.link} className="mt-4 text-[14px] font-semibold text-[#111827] hover:text-[#EF4949] transition-colors">
                      View {integration.name} Integration →
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </section>

          <Section id="security" title="Secure by Design">
            <p>Bellhopt only uses the data needed to run the arrival layer and fulfill guest requests. Access is controlled, and operational actions are logged.</p>
            <ul className="list-disc pl-6 mt-4">
              <li>Least-privilege access</li>
              <li>Environment-based configuration</li>
              <li>No sensitive data exposed in the guest flow</li>
            </ul>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Make Arrival-Ready the Standard, Without Changing Your Stack
            </h2>
            <p className="mt-3 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Bellhopt plugs into what you already use and adds the concierge execution layer your PMS cannot.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Schedule A Demo</a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>Talk To Sales</a>
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/integrations/streamline">Streamline integration</Link></li>
              <li><Link className="text-primary" to="/for/property-managers">For property managers</Link></li>
              <li><Link className="text-primary" to="/for/enterprise-property-managers">Enterprise property managers</Link></li>
              <li><Link className="text-primary" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
}

export default IntegrationsPage;
